import * as jwt from "jsonwebtoken";
import { Fight, FightOverview, Grade } from "./Interfaces";

const baseURL = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

// todo: validation
export const hasValidToken = () => {
	const token = localStorage.getItem("sypt_token");
	if (!token) {
		return false;
	}
	const decodedToken = jwt.decode(token, { complete: true }) as any;
	const dateNow = new Date();

	if (decodedToken.payload.exp < dateNow.getTime() / 1000) {
		console.log("expired token");
		return false;
	}
	console.log(
		"token valid for",
		Math.floor(decodedToken.payload.exp - dateNow.getTime() / 1000),
		"seconds"
	);
	return true;
};
export const setToken = (newToken: string) => {
	localStorage.setItem("sypt_token", newToken);
};
export const timeUntilRefresh = () => {
	const token = localStorage.getItem("sypt_token");
	if (!token) {
		return 0;
	}
	const decodedToken = jwt.decode(token, { complete: true }) as any;
	const timeOutTime = decodedToken.payload.exp;
	const issueTime = decodedToken.payload.iat;
	const timeNow = Math.floor(new Date().getTime() / 1000);

	const refreshTime = (timeOutTime + issueTime) / 2;
	return Math.max(0, refreshTime - timeNow) * 1000;
};

const getData = async (route: string) => {
	const res = await fetch(baseURL + route, {
		headers: {
			Authorization: "Bearer " + localStorage.getItem("sypt_token"),
		},
	});
	const data = await res.json();
	return data;
};
const getDataWithParam = async (route: string, value: number) => {
	const res = await fetch(baseURL + route + "/" + value, {
		headers: {
			Authorization: "Bearer " + localStorage.getItem("sypt_token"),
		},
	});
	const data = await res.json();
	return data;
};

const sendData = async (route: string, payload: object, method: string = "post") => {
	const res = await fetch(baseURL + route, {
		method,
		headers: {
			Authorization: "Bearer " + localStorage.getItem("sypt_token"),
			Accept: "application/json, text/plain, */*",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	return await res.json();
};

// public routes, no authentication
export const login = (password: string) => sendData("/login", { password });
export const getJurors = async () => (await getData("/jurors")).jurors;
export const getTeams = async () => (await getData("/teams")).teams;
export const getAllFights = async (): Promise<FightOverview[]> =>
	(await getData("/allFights")).fights;
export const getActiveFights = async () => (await getData("/allActiveFights")).fights;
export const getAllParticipants = async () => (await getData("/participants")).participants;

export const getCheckedOrUncheckedStages = async (checked: boolean) =>
	await getDataWithParam("/checkedOrUncheckedStages", checked ? 1 : 0);
export const getFight = async (fightId: number): Promise<Fight> =>
	await getDataWithParam("/fight", fightId);
export const getFightOverview = async (fightId: number) =>
	await getDataWithParam("/fightOverview", fightId);
export const getTeamRanking = async (round: number) =>
	await getDataWithParam("/teamRanking", round);

// fight assistant routes, password authentication
export const checkPassword = async (fight_id: number, password: string) =>
	sendData("/checkPassword", { fight_id, password });
export const sendJurorOrder = async (
	fight_id: number,
	password: string,
	orderData: {
		fight_id: number;
		jurorOrder: Array<{ juror_id: number; position: number }>;
	}
) => await sendData("/setJurorOrder", { fight_id, password, ...orderData });
export const setCurrentPart = async (fight_id: number, password: string, currentPart: number) =>
	sendData("/setCurrentPart", { fight_id, password, currentPart });
export const setCurrentPhase = (fight_id: number, password: string, currentPhase: number) =>
	sendData("/setCurrentPhase", { fight_id, password, currentPhase });
export const setStageFinished = (fight_id: number, password: string, stage_id: number) =>
	sendData("/setStageFinished", { fight_id, password, stage_id });
export const sendGrades = async (
	fight_id: number,
	password: string,
	stage_id: number,
	grades: Grade[]
) => sendData("/submitGrades", { fight_id, password, stage_id, grades });

// backend routes, token authentication
export const getAllPasswords = async () => (await getData("/passwords")).passwords;
export const setStageChecked = (stage_id: number) => sendData("/setStageChecked", { stage_id });
export const deleteJurorFromFight = async (fight_id: number, juror_id: number) =>
	sendData("/deleteJurorFromFight", { fight_id, juror_id }, "delete");
export const addJurorToFight = async (fight_id: number, juror_id: number, position: number) =>
	sendData("/addJurorToFight", { fight_id, juror_id, position });
export const setAbsent = async (participantId: number, repopprev: number, value: boolean) =>
	sendData("/setAbsent", { participantId, repopprev, value });
export const setTeamName = (team_id: number, team_name: string) =>
	sendData("/setTeamName", { team_id, team_name });
export const setParticipantLastname = (participant_id: number, lastname: string) =>
	sendData("/setParticipantLastname", { participant_id, lastname });
export const setParticipantFirstname = (participant_id: number, firstname: string) =>
	sendData("/setParticipantFirstname", { participant_id, firstname });
export const setJurorLastname = (juror_id: number, lastname: string) =>
	sendData("/setJurorLastname", { juror_id, lastname });
export const setJurorFirstname = (juror_id: number, firstname: string) =>
	sendData("/setJurorFirstname", { juror_id, firstname });
export const createJuror = (lastname: string, firstname: string) =>
	sendData("/createJuror", { lastname, firstname });
export const changeGrade = (
	oldGrade: number,
	newGrade: number,
	stage_id: number,
	participant_id: number,
	juror_id: number
) => sendData("/changeGrade", { oldGrade, newGrade, stage_id, participant_id, juror_id });
export const changeGradeJuror = (stage_id: number, old_juror_id: number, new_juror_id: number) =>
	sendData("/changeGradeJuror", { stage_id, old_juror_id, new_juror_id });
export const setGradeShouldCount = (stage_id: number, juror_id: number, grade_counts: boolean) =>
	sendData("/setGradeShouldCount", { stage_id, juror_id, grade_counts });
export const sendJurorOrderBackend = async (orderData: {
	fight_id: number;
	jurorOrder: Array<{ juror_id: number; position: number }>;
}) => await sendData("/setJurorOrderBackend", orderData);
export const resetFight = (fight_id: number) => sendData("/resetFight", { fight_id });

export const refresh = async () => {
	const res = await fetch(baseURL + "/refreshToken", {
		method: "POST",
		headers: {
			Authorization: "Bearer " + localStorage.getItem("sypt_token"),
			Accept: "application/json, text/plain, */*",
			"Content-Type": "application/json",
		},
	});
	if (res.status !== 200) {
		throw new Error("failed to refresh token");
	}
	const resJson = await res.json();
	return resJson.token;
};



// WEBPACK FOOTER //
// ./src/api.tsx